import React, { useState, useEffect, useContext } from 'react';
import { ServerConfigContext } from '../context/ServerConfigContext';
import './TradingPages.css';

const AlgorithmThemeCalendar = () => {
  const { getApiUrl, isConfigLoaded } = useContext(ServerConfigContext);

  // 쿠키 관련 함수들
  const saveDateToCookie = (date) => {
    const yearMonth = `${date.getFullYear()}-${date.getMonth()}`;
    document.cookie = `themeCalendarDate=${yearMonth}; max-age=${7 * 24 * 60 * 60}; path=/`; // 7일간 저장
    console.log(`📅 [Cookie] 달력 날짜 저장: ${yearMonth}`);
  };

  const loadDateFromCookie = () => {
    const cookies = document.cookie.split(';');
    const dateCookie = cookies.find(cookie => cookie.trim().startsWith('themeCalendarDate='));

    if (dateCookie) {
      const yearMonth = dateCookie.split('=')[1];
      const [year, month] = yearMonth.split('-').map(Number);

      if (!isNaN(year) && !isNaN(month)) {
        const savedDate = new Date(year, month, 1);
        console.log(`📅 [Cookie] 달력 날짜 복원: ${year}년 ${month + 1}월`);
        return savedDate;
      }
    }

    console.log(`📅 [Cookie] 저장된 달력 날짜 없음, 현재 날짜 사용`);
    return new Date();
  };

  // 현재 날짜 상태 (쿠키에서 복원)
  const [currentDate, setCurrentDate] = useState(() => loadDateFromCookie());
  const [selectedDate, setSelectedDate] = useState(null);

  // 테마 데이터 상태
  const [monthlyThemes, setMonthlyThemes] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedThemes, setSelectedThemes] = useState([]);

  // 날짜별 상세 종목 정보 상태
  const [selectedDateDetails, setSelectedDateDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // 월별 테마 데이터 가져오기
  const fetchMonthlyThemes = async (year, month) => {
    try {
      setLoading(true);

      // 해당 월의 시작일과 종료일 계산
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0); // 다음 달 0일 = 이번 달 마지막 날
      const today = new Date();

      // 종료일이 오늘보다 미래면 오늘로 설정
      const actualEndDate = endDate > today ? today : endDate;

      // YYYYMMDD 형식으로 변환
      const startDateStr = formatDateToAPI(startDate);
      const endDateStr = formatDateToAPI(actualEndDate);

      console.log(`📅 ${year}년 ${month + 1}월 테마 데이터 요청:`, {
        startDate: startDateStr,
        endDate: endDateStr
      });

      const alertApiUrl = getApiUrl('alert-api-server', '/analysis/themes');
      const requestBody = {
        end_date: endDateStr,
        start_date: startDateStr
      };

      const response = await fetch(alertApiUrl, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      console.log(`📡 ${year}년 ${month + 1}월 API 응답:`, result);
      console.log(`🔍 응답 상태 확인:`, {
        success: result.success,
        hasData: !!result.data,
        hasDates: !!(result.data && result.data.dates),
        datesLength: result.data?.dates?.length || 0,
        dates: result.data?.dates || []
      });

      if (result.success && result.data) {
        const monthKey = `${year}-${month}`;

        // dates 배열을 날짜 키 기반으로 변환
        const dateMap = {};
        if (result.data.dates && Array.isArray(result.data.dates)) {
          result.data.dates.forEach(dateItem => {
            if (dateItem.date) {
              dateMap[dateItem.date] = {
                date: dateItem.date,
                themes: dateItem.themes || [],
                total_themes: dateItem.total_themes || dateItem.themes?.length || 0,
                total_results: dateItem.total_results || 0
              };
            }
          });
        }

        const processedData = {
          ...result.data,
          dateMap: dateMap, // 날짜별 데이터 맵
          dates: result.data.dates || []
        };

        setMonthlyThemes(prev => ({
          ...prev,
          [monthKey]: processedData
        }));

        console.log(`✅ ${year}년 ${month + 1}월 테마 데이터 로드 완료:`, {
          totalDates: result.data.dates?.length || 0,
          dateMapKeys: Object.keys(dateMap),
          startDate: processedData.start_date,
          endDate: processedData.end_date,
          monthKey: monthKey,
          dateMap: dateMap
        });
      } else {
        console.warn(`⚠️ ${year}년 ${month + 1}월 테마 데이터 없음:`, result);
        if (result.error) {
          console.error(`❌ API 오류:`, result.error);
        }
      }

    } catch (error) {
      console.error(`❌ ${year}년 ${month + 1}월 테마 데이터 조회 실패:`, error);
    } finally {
      setLoading(false);
    }
  };

  // 날짜를 YYYYMMDD 형식으로 변환
  const formatDateToAPI = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // 특정 날짜의 상세 종목 정보 가져오기
  const fetchDateDetails = async (date) => {
    try {
      setLoadingDetails(true);
      const dateStr = formatDateToAPI(date);

      console.log(`📊 [날짜 상세] ${dateStr} 종목 정보 요청 시작`);

      const alertApiUrl = getApiUrl('alert-api-server', '/analysis/batch/cached');
      const requestBody = {
        analyzed_date: dateStr
      };

      const response = await fetch(alertApiUrl, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      console.log(`📡 [날짜 상세] ${dateStr} API 응답:`, result);

      if (result.success && result.data) {
        setSelectedDateDetails(result.data);
        console.log(`✅ [날짜 상세] ${dateStr} 종목 정보 로드 완료:`, {
          totalResults: result.data.total_results,
          totalTimeSlots: result.data.total_time_slots,
          executionTime: result.data.execution_time_ms
        });
      } else {
        console.warn(`⚠️ [날짜 상세] ${dateStr} 데이터 없음:`, result);
        setSelectedDateDetails(null);
        if (result.error) {
          console.error(`❌ [날짜 상세] API 오류:`, result.error);
        }
      }

    } catch (error) {
      console.error(`❌ [날짜 상세] ${formatDateToAPI(date)} 종목 정보 조회 실패:`, error);
      setSelectedDateDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  // 도움말 팝업 상태
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  // 현재 월 변경 시 데이터 로드
  useEffect(() => {
    if (isConfigLoaded) {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      fetchMonthlyThemes(year, month);
    }
  }, [currentDate, isConfigLoaded]);

  // 미리 정의된 색상 팔레트 (20개)
  const predefinedColors = [
    '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
    '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
    '#393b79', '#637939', '#8c6d31', '#843c39', '#7b4173',
    '#5254a3', '#8ca252', '#bd9e39', '#ad494a', '#a55194'
  ];

  // 테마별 색상 캐시 (일관성 유지)
  const [themeColorCache, setThemeColorCache] = useState({});
  const [usedColorIndices, setUsedColorIndices] = useState(new Set());

  // 테마별 고유 색상 생성
  const generateThemeColor = (themeName) => {
    // 캐시에 있으면 반환
    if (themeColorCache[themeName]) {
      return themeColorCache[themeName];
    }

    let selectedColor;

    // 먼저 사전 정의된 색상 중 사용하지 않은 것 찾기
    if (usedColorIndices.size < predefinedColors.length) {
      // 테마 이름을 해시값으로 변환하여 일관된 색상 인덱스 생성
      let hash = 0;
      for (let i = 0; i < themeName.length; i++) {
        hash = themeName.charCodeAt(i) + ((hash << 5) - hash);
      }

      let colorIndex = Math.abs(hash) % predefinedColors.length;

      // 이미 사용된 색상이면 다음 사용 가능한 색상 찾기
      while (usedColorIndices.has(colorIndex)) {
        colorIndex = (colorIndex + 1) % predefinedColors.length;
      }

      selectedColor = predefinedColors[colorIndex];
      setUsedColorIndices(prev => new Set([...prev, colorIndex]));
    } else {
      // 사전 정의된 색상을 모두 사용한 경우 랜덤 색상 생성
      let hash = 0;
      for (let i = 0; i < themeName.length; i++) {
        hash = themeName.charCodeAt(i) + ((hash << 5) - hash);
      }

      const hue = Math.abs(hash) % 360;
      const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
      const lightness = 45 + (Math.abs(hash) % 15);  // 45-60%

      selectedColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    }

    // 캐시에 저장
    setThemeColorCache(prev => ({
      ...prev,
      [themeName]: selectedColor
    }));

    return selectedColor;
  };

  // 현재 월의 모든 테마별 출현 날짜 계산
  const getThemeAppearanceDates = () => {
    const monthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
    const monthData = monthlyThemes[monthKey];

    if (!monthData || !monthData.dateMap) {
      return {};
    }

    const themeAppearances = {};

    Object.entries(monthData.dateMap).forEach(([dateKey, dateData]) => {
      if (dateData.themes && dateData.themes.length > 0) {
        dateData.themes.forEach(theme => {
          if (!themeAppearances[theme.theme]) {
            themeAppearances[theme.theme] = {
              dates: [],
              totalCount: 0,
              maxCount: 0,
              color: generateThemeColor(theme.theme)
            };
          }

          themeAppearances[theme.theme].dates.push({
            date: dateKey,
            count: theme.count || 0
          });
          themeAppearances[theme.theme].totalCount += theme.count || 0;
          themeAppearances[theme.theme].maxCount = Math.max(
            themeAppearances[theme.theme].maxCount,
            theme.count || 0
          );
        });
      }
    });

    // 각 테마의 날짜를 count 기준으로 정렬
    Object.values(themeAppearances).forEach(theme => {
      theme.dates.sort((a, b) => b.count - a.count);
    });

    return themeAppearances;
  };

  // 달력 렌더링을 위한 날짜 배열 생성
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);

    // 주의 시작을 일요일로 맞추기
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const days = [];
    const current = new Date(startDate);

    // 6주간의 날짜 생성 (42일)
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // 특정 날짜의 테마 개수 가져오기
  const getThemeCountForDate = (date) => {
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    const monthData = monthlyThemes[monthKey];

    if (!monthData || !monthData.dateMap) {
      return 0;
    }

    // 날짜를 YYYYMMDD 형식으로 변환
    const dateKey = formatDateToAPI(date);
    const dateData = monthData.dateMap[dateKey];

    if (!dateData) {
      return 0;
    }

    const themeCount = dateData.total_themes || dateData.themes?.length || 0;

    return themeCount;
  };

  // 특정 날짜의 상위 3개 테마 가져오기 (count 순으로 정렬)
  const getTopThemesForDate = (date) => {
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    const monthData = monthlyThemes[monthKey];

    if (!monthData || !monthData.dateMap) {
      return [];
    }

    // 날짜를 YYYYMMDD 형식으로 변환
    const dateKey = formatDateToAPI(date);
    const dateData = monthData.dateMap[dateKey];

    if (!dateData || !dateData.themes) {
      return [];
    }

    // count 기준으로 내림차순 정렬하고 상위 3개만 반환
    return dateData.themes
      .slice() // 원본 배열 변경 방지
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .slice(0, 3);
  };

  // 날짜 클릭 핸들러
  const handleDateClick = async (date) => {
    setSelectedDate(date);

    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    const monthData = monthlyThemes[monthKey];

    if (monthData && monthData.dateMap) {
      const dateKey = formatDateToAPI(date);
      const dateData = monthData.dateMap[dateKey];

      if (dateData && dateData.themes) {
        setSelectedThemes(dateData.themes);
        console.log(`🎯 ${dateKey} 날짜 테마 선택:`, dateData.themes);

        // 상세 종목 정보 가져오기
        await fetchDateDetails(date);
      } else {
        setSelectedThemes([]);
        setSelectedDateDetails(null);
        console.log(`📭 ${dateKey} 날짜에 테마 데이터 없음`);
      }
    } else {
      setSelectedThemes([]);
      setSelectedDateDetails(null);
    }
  };

  // 이전/다음 월 이동
  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);

      // 새로운 날짜를 쿠키에 저장
      saveDateToCookie(newDate);

      return newDate;
    });
    setSelectedDate(null);
    setSelectedThemes([]);
  };

  // 년도/월 직접 설정
  const handleYearChange = (year) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(parseInt(year));

    // 새로운 날짜를 쿠키에 저장
    saveDateToCookie(newDate);

    setCurrentDate(newDate);
    setSelectedDate(null);
    setSelectedThemes([]);
  };

  const handleMonthChange = (month) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(parseInt(month));

    // 새로운 날짜를 쿠키에 저장
    saveDateToCookie(newDate);

    setCurrentDate(newDate);
    setSelectedDate(null);
    setSelectedThemes([]);
  };

  // 년도 옵션 생성 (현재 년도 기준 ±3년)
  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 3; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years;
  };

  // 월 옵션 생성
  const generateMonthOptions = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      months.push({
        value: i,
        label: `${i + 1}월`
      });
    }
    return months;
  };

  const calendarDays = generateCalendarDays();
  const today = new Date();
  const monthKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}`;
  const currentMonthData = monthlyThemes[monthKey];

  console.log(`📅 달력 렌더링 정보:`, {
    currentDate: currentDate.toDateString(),
    monthKey,
    hasCurrentMonthData: !!currentMonthData,
    monthlyThemesKeys: Object.keys(monthlyThemes),
    currentMonthData: currentMonthData
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title-section">
          <h1>📅 테마 달력</h1>
          <button
            className="help-button"
            onClick={() => setShowHelpPopup(true)}
            aria-label="도움말"
          >
            ❓
          </button>
        </div>
        <p>월별 테마 분석 결과를 달력으로 확인하세요</p>
      </div>

      <div className="calendar-layout">
        {/* 왼쪽: 달력 영역 */}
        <div className="calendar-section">
          <div className="calendar-container">
            <div className="calendar-header">
              <button
                onClick={() => navigateMonth(-1)}
                className="calendar-nav-btn"
                disabled={loading}
              >
                ← 이전 달
              </button>

              <div className="calendar-title-section">
                <div className="date-selectors">
                  <select
                    className="year-selector"
                    value={currentDate.getFullYear()}
                    onChange={(e) => handleYearChange(e.target.value)}
                    disabled={loading}
                  >
                    {generateYearOptions().map(year => (
                      <option key={year} value={year}>{year}년</option>
                    ))}
                  </select>

                  <select
                    className="month-selector"
                    value={currentDate.getMonth()}
                    onChange={(e) => handleMonthChange(e.target.value)}
                    disabled={loading}
                  >
                    {generateMonthOptions().map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>

                {loading && <span className="loading-indicator"> 🔄</span>}
              </div>

              <button
                onClick={() => navigateMonth(1)}
                className="calendar-nav-btn"
                disabled={loading}
              >
                다음 달 →
              </button>
            </div>

            {/* 요일 헤더 */}
            <div className="calendar-weekdays">
              {['일', '월', '화', '수', '목', '금', '토'].map(day => (
                <div key={day} className="calendar-weekday">{day}</div>
              ))}
            </div>

            {/* 달력 본체 */}
            <div className="calendar-grid">
              {calendarDays.map((date, index) => {
                const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                const isToday = date.toDateString() === today.toDateString();
                const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                const themeCount = isCurrentMonth ? getThemeCountForDate(date) : 0;
                const topThemes = isCurrentMonth ? getTopThemesForDate(date) : [];
                const isPastDate = date < today;

                return (
                  <div
                    key={index}
                    className={`calendar-day ${
                      isCurrentMonth ? 'current-month' : 'other-month'
                    } ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${
                      isPastDate && isCurrentMonth ? 'has-data' : ''
                    }`}
                    onClick={() => isCurrentMonth && handleDateClick(date)}
                  >
                    <div className="day-header">
                      <span className="day-number">{date.getDate()}</span>
                      {isCurrentMonth && themeCount > 0 && (
                        <span className="theme-count-badge">{themeCount}</span>
                      )}
                    </div>
                    {isCurrentMonth && topThemes.length > 0 && (
                      <div className="theme-preview">
                        {topThemes.map((theme, themeIndex) => (
                          <div key={themeIndex} className="theme-rank-item">
                            <span className="rank-number">{themeIndex + 1}</span>
                            <span
                              className="theme-name-preview"
                              style={{ color: generateThemeColor(theme.theme) }}
                            >
                              {theme.theme}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* 오른쪽: 정보 영역 */}
        <div className="info-section">
          {/* 월별 요약 정보 */}
          {currentMonthData && (
            <div className="monthly-summary">
              <h3>📊 월별 요약</h3>
              <div className="summary-cards">
                <div className="summary-card">
                  <span className="summary-label">데이터 있는 날짜</span>
                  <span className="summary-value">{currentMonthData.dates?.length || 0}일</span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">총 테마 수</span>
                  <span className="summary-value">
                    {currentMonthData.dateMap ?
                      Object.values(currentMonthData.dateMap).reduce((sum, dateData) =>
                        sum + (dateData.total_themes || 0), 0
                      ) : 0}개
                  </span>
                </div>
                <div className="summary-card">
                  <span className="summary-label">조회 기간</span>
                  <span className="summary-value">
                    {currentMonthData.start_date?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')} ~ {currentMonthData.end_date?.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* 선택된 날짜의 테마 상세 정보 */}
          {selectedDate && selectedThemes.length > 0 ? (
            <div className="theme-details">
              <div className="theme-details-header">
                <h3>
                  📊 {selectedDate.getFullYear()}년 {selectedDate.getMonth() + 1}월 {selectedDate.getDate()}일 테마 상세
                  {loadingDetails && <span className="loading-indicator"> 🔄</span>}
                </h3>
                <button
                  onClick={() => {
                    setSelectedDate(null);
                    setSelectedThemes([]);
                    setSelectedDateDetails(null);
                  }}
                  className="close-details-btn"
                >
                  ✕
                </button>
              </div>

              <div className="theme-list">
                {selectedThemes.map((theme, index) => (
                  <div key={index} className="theme-item">
                    <div className="theme-info">
                      <span
                        className="theme-name"
                        style={{ color: generateThemeColor(theme.theme) }}
                      >
                        {theme.theme}
                      </span>
                      <span className="theme-stats">
                        {theme.count}회 언급 · {theme.unique_stocks}개 종목
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* 시간대별 종목 상세 정보 */}
              {selectedDateDetails && (
                <div className="date-details-section">
                  <div className="date-details-header">
                    <h4>🕒 시간대별 종목 상세 ({selectedDateDetails.total_results}개 결과)</h4>
                    <div className="execution-info">
                      <span>실행시간: {selectedDateDetails.execution_time_ms}ms</span>
                      <span>시간구간: {selectedDateDetails.total_time_slots}개</span>
                    </div>
                  </div>

                  <div className="time-slots-container">
                    {selectedDateDetails.time_slots?.map((timeSlot, index) => (
                      <div key={index} className="time-slot">
                        <div className="time-slot-header">
                          <h5>⏰ {timeSlot.analyzed_time?.replace(/(\d{2})(\d{2})(\d{2})/, '$1:$2:$3')}</h5>
                          <span className="result-count">{timeSlot.result_count}개 결과</span>
                        </div>

                        <div className="stocks-grid">
                          {timeSlot.results?.map((stock, stockIndex) => (
                            <div key={stockIndex} className="stock-card">
                              <div className="stock-header">
                                <span className="stock-rank">#{stock.rank}</span>
                                <span className="stock-name">{stock.stock_name}</span>
                                <span className="stock-code">({stock.stock_code})</span>
                              </div>

                              <div className="stock-theme">
                                <span
                                  className="theme-badge"
                                  style={{
                                    backgroundColor: generateThemeColor(stock.theme),
                                    color: 'white'
                                  }}
                                >
                                  {stock.theme}
                                </span>
                              </div>

                              <div className="stock-metrics">
                                <div className="metric">
                                  <span className="metric-label">가격:</span>
                                  <span className="metric-value">{stock.price?.toLocaleString()}원</span>
                                </div>
                                <div className="metric">
                                  <span className="metric-label">변동률:</span>
                                  <span className={`metric-value ${stock.diff >= 0 ? 'positive' : 'negative'}`}>
                                    {stock.diff >= 0 ? '+' : ''}{stock.diff}%
                                  </span>
                                </div>
                                <div className="metric">
                                  <span className="metric-label">거래대금:</span>
                                  <span className="metric-value">{(stock.value / 100000000).toFixed(1)}억</span>
                                </div>
                                <div className="metric">
                                  <span className="metric-label">눌림일:</span>
                                  <span className="metric-value">{stock.nullim_days}일</span>
                                </div>
                              </div>

                              {stock.telegram_sent && (
                                <div className="telegram-badge">📱 텔레그램 발송</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* 테마별 출현 날짜 표시 */
            <div className="theme-appearances">
              <h3>📈 이번 달 테마별 출현 날짜</h3>
              <div className="theme-appearances-list">
                {Object.entries(getThemeAppearanceDates())
                  .sort(([,a], [,b]) => b.totalCount - a.totalCount)
                  .slice(0, 10) // 상위 10개만 표시
                  .map(([themeName, themeData]) => (
                    <div key={themeName} className="theme-appearance-item">
                      <div className="theme-appearance-header">
                        <span
                          className="theme-appearance-name"
                          style={{
                            color: themeData.color,
                            fontWeight: 'bold'
                          }}
                        >
                          {themeName}
                        </span>
                        <span className="theme-appearance-stats">
                          총 {themeData.totalCount}회 · {themeData.dates.length}일
                        </span>
                      </div>
                      <div className="theme-appearance-dates">
                        {themeData.dates.slice(0, 5).map((dateInfo, index) => (
                          <span
                            key={index}
                            className="appearance-date-badge"
                            style={{ borderColor: themeData.color }}
                          >
                            {dateInfo.date.replace(/(\d{4})(\d{2})(\d{2})/, '$2/$3')} ({dateInfo.count})
                          </span>
                        ))}
                        {themeData.dates.length > 5 && (
                          <span className="more-dates">+{themeData.dates.length - 5}일</span>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 도움말 팝업 */}
      {showHelpPopup && (
        <div className="help-popup-overlay" onClick={() => setShowHelpPopup(false)}>
          <div className="help-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="help-popup-header">
              <h3>💡 테마 달력 사용법</h3>
              <button
                className="help-popup-close"
                onClick={() => setShowHelpPopup(false)}
                aria-label="닫기"
              >
                ✕
              </button>
            </div>
            <div className="help-popup-body">
              <ul>
                <li>📅 <strong>월 이동</strong>: 달력 헤더의 화살표 버튼으로 원하는 월로 이동하세요</li>
                <li>🔢 <strong>테마 개수</strong>: 각 날짜 우상단 숫자는 해당 날짜의 테마 개수입니다</li>
                <li>🏆 <strong>테마 순위</strong>: 날짜 내 1, 2, 3위 테마가 색상별로 표시됩니다</li>
                <li>🖱️ <strong>날짜 클릭</strong>: 날짜를 클릭하면 해당 날짜의 상세 테마 목록을 볼 수 있습니다</li>
                <li>🎨 <strong>색상 시스템</strong>: 각 테마는 고유한 색상으로 표시되어 일관성을 유지합니다</li>
                <li>📊 <strong>월별 통계</strong>: 오른쪽 패널에서 이번 달 테마별 출현 날짜를 확인하세요</li>
                <li>⏰ <strong>데이터 범위</strong>: 오늘 이후의 날짜는 데이터가 없습니다</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlgorithmThemeCalendar;